import { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import { Bell, Clock, X, Car, MapPin, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

export default function RideCountdownNotification({ token, rides = [], onRideClick }) {
  const [upcomingRides, setUpcomingRides] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const audioRef = useRef(null);
  const checkIntervalRef = useRef(null);

  useEffect(() => {
    // Check for upcoming rides every minute
    checkUpcomingRides();
    checkIntervalRef.current = setInterval(checkUpcomingRides, 60000);

    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [rides]);

  const checkUpcomingRides = () => {
    const now = new Date();
    const thirtyMinutes = 30 * 60 * 1000;
    const fifteenMinutes = 15 * 60 * 1000;
    const fiveMinutes = 5 * 60 * 1000;

    const upcoming = rides
      .filter(ride => {
        if (!ride.scheduled_time || dismissed.includes(ride.ride_id)) return false;
        if (!["assigned", "accepted"].includes(ride.ride_status)) return false;
        
        const scheduledTime = new Date(ride.scheduled_time);
        const timeDiff = scheduledTime - now;
        
        // Within 30 minutes
        return timeDiff > 0 && timeDiff <= thirtyMinutes;
      })
      .map(ride => {
        const scheduledTime = new Date(ride.scheduled_time);
        const timeDiff = scheduledTime - now;
        const minutesLeft = Math.floor(timeDiff / 60000);
        
        let urgency = "normal";
        if (timeDiff <= fiveMinutes) urgency = "critical";
        else if (timeDiff <= fifteenMinutes) urgency = "warning";
        
        return {
          ...ride,
          minutesLeft,
          urgency,
          formattedTime: scheduledTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
      })
      .sort((a, b) => a.minutesLeft - b.minutesLeft);

    setUpcomingRides(upcoming);
    
    // Show notification if there are upcoming rides
    if (upcoming.length > 0 && !showNotification) {
      setShowNotification(true);
      
      // Send browser notification for critical rides
      const criticalRide = upcoming.find(r => r.urgency === "critical");
      if (criticalRide) {
        sendBrowserNotification(criticalRide);
        playNotificationSound();
      }
    }
  };

  const sendBrowserNotification = (ride) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification("Ride Starting Soon!", {
        body: `Your ride to ${ride.drop_location} starts in ${ride.minutesLeft} minutes!`,
        icon: "/favicon.ico",
        tag: `ride-${ride.ride_id}`,
        requireInteraction: true
      });
    }
  };

  const playNotificationSound = () => {
    // Create a simple beep using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = "sine";
      gainNode.gain.value = 0.3;
      
      oscillator.start();
      setTimeout(() => oscillator.stop(), 200);
    } catch (e) {
      console.log("Audio notification not supported");
    }
  };

  const handleDismiss = (rideId) => {
    setDismissed(prev => [...prev, rideId]);
    const remaining = upcomingRides.filter(r => r.ride_id !== rideId);
    setUpcomingRides(remaining);
    if (remaining.length === 0) {
      setShowNotification(false);
    }
  };

  const handleDismissAll = () => {
    setDismissed(prev => [...prev, ...upcomingRides.map(r => r.ride_id)]);
    setUpcomingRides([]);
    setShowNotification(false);
  };

  if (!showNotification || upcomingRides.length === 0) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 z-50 max-w-sm w-full animate-in slide-in-from-bottom-5"
      data-testid="ride-countdown-notification"
    >
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-forest to-forest-dark px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Bell className="w-5 h-5 animate-pulse" />
            <span className="font-semibold">Upcoming Ride{upcomingRides.length > 1 ? "s" : ""}</span>
          </div>
          <button
            onClick={handleDismissAll}
            className="text-white/70 hover:text-white p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Rides List */}
        <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
          {upcomingRides.map((ride) => (
            <div 
              key={ride.ride_id}
              className={`p-4 ${
                ride.urgency === "critical" ? "bg-red-50" : 
                ride.urgency === "warning" ? "bg-amber-50" : "bg-white"
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    ride.urgency === "critical" ? "bg-red-500" :
                    ride.urgency === "warning" ? "bg-amber-500" : "bg-forest"
                  } text-white`}>
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <p className={`font-bold text-lg ${
                      ride.urgency === "critical" ? "text-red-600" :
                      ride.urgency === "warning" ? "text-amber-600" : "text-forest"
                    }`}>
                      {ride.minutesLeft} min
                    </p>
                    <p className="text-xs text-slate-500">Scheduled: {ride.formattedTime}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDismiss(ride.ride_id)}
                  className="text-slate-400 hover:text-slate-600 p-1"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-1 text-sm mb-3">
                <div className="flex items-center gap-2 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="truncate">{ride.pickup_location}</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="truncate">{ride.drop_location}</span>
                </div>
              </div>

              {ride.driver_name && (
                <p className="text-xs text-slate-500 mb-3">
                  <Car className="w-3 h-3 inline mr-1" />
                  Driver: {ride.driver_name}
                </p>
              )}

              {ride.urgency === "critical" && (
                <div className="flex items-center gap-1 text-red-600 text-xs font-medium mb-2">
                  <AlertCircle className="w-3 h-3" />
                  Get ready! Your ride is almost here
                </div>
              )}

              <Button
                size="sm"
                onClick={() => onRideClick && onRideClick(ride)}
                className="w-full rounded-full bg-forest hover:bg-forest-dark text-white text-xs"
                data-testid={`view-ride-${ride.ride_id}`}
              >
                View Ride Details
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Hook for managing countdown notifications
export function useRideCountdown(token) {
  const [rides, setRides] = useState([]);

  useEffect(() => {
    if (!token) return;
    
    const fetchRides = async () => {
      try {
        const res = await safeFetch(`${API}/customer/rides`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Filter only scheduled rides
          setRides(data.filter(r => r.scheduled_time && ["assigned", "accepted"].includes(r.ride_status)));
        }
      } catch (err) {
        console.error("Failed to fetch rides for countdown:", err);
      }
    };

    fetchRides();
    const interval = setInterval(fetchRides, 5 * 60 * 1000); // Every 5 minutes
    
    return () => clearInterval(interval);
  }, [token]);

  return rides;
}
